import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPatch, apiDelete, getAuthHeaders } from '../../lib/api'
import { 
  Award, 
  ArrowLeft, 
  Edit2, 
  Trash2, 
  Archive,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Building2,
  FileText,
  ExternalLink,
  MoreVertical
} from 'lucide-react'

interface Certification {
  id: string
  certName: string
  issuer: string
  issueDate: string | null
  expirationDate: string | null
  status: 'active' | 'expiring_soon' | 'expired' | 'archived'
  documentUri?: string
  certNumber?: string
  state?: string
  notes?: string
}

export default function CertificationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [documentBlobUrl, setDocumentBlobUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['/api/certifications', id],
    queryFn: () => apiGet<{ certification: Certification }>(`/certifications/${id}`),
    enabled: !!id,
  })

  const archiveMutation = useMutation({
    mutationFn: () => apiPatch(`/certifications/${id}`, { status: 'archived' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/certifications'] })
      navigate('/')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: () => apiDelete(`/certifications/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/certifications'] })
      navigate('/')
    },
  })

  const cert = data?.certification

  useEffect(() => {
    if (cert?.documentUri) {
      setImageLoading(true)
      fetch(cert.documentUri, {
        headers: getAuthHeaders(),
        credentials: 'include',
      })
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load image')
          return res.blob()
        })
        .then((blob) => {
          const url = URL.createObjectURL(blob)
          setDocumentBlobUrl(url)
        })
        .catch((err) => {
          console.error('Failed to load document image:', err)
        })
        .finally(() => {
          setImageLoading(false)
        })
    }
    return () => {
      if (documentBlobUrl) {
        URL.revokeObjectURL(documentBlobUrl)
      }
    }
  }, [cert?.documentUri])

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 text-sm font-medium rounded-full">
            <CheckCircle className="w-4 h-4" />
            Active
          </span>
        )
      case 'expiring_soon':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-700 text-sm font-medium rounded-full">
            <Clock className="w-4 h-4" />
            Expiring Soon
          </span>
        )
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded-full">
            <AlertTriangle className="w-4 h-4" />
            Expired
          </span>
        )
      case 'archived':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-600 text-sm font-medium rounded-full">
            <Archive className="w-4 h-4" />
            Archived
          </span>
        )
      default:
        return null
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No expiration'
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const getTimeUntilExpiration = (dateString: string | null) => {
    if (!dateString) return { text: '', color: '' }
    const now = new Date()
    const expDate = new Date(dateString)
    const diffMs = expDate.getTime() - now.getTime()
    const totalDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
    
    if (diffMs < 0) {
      const pastMs = Math.abs(diffMs)
      const days = Math.floor(pastMs / (1000 * 60 * 60 * 24))
      let text = ''
      if (days < 30) text = `${days} day${days !== 1 ? 's' : ''} ago`
      else {
        const months = Math.floor(days / 30)
        if (months < 12) text = `${months} month${months !== 1 ? 's' : ''} ago`
        else {
          const years = Math.floor(months / 12)
          text = `${years} year${years !== 1 ? 's' : ''} ago`
        }
      }
      return { text, color: 'text-red-600' }
    }
    
    const years = Math.floor(totalDays / 365)
    const remainingDaysAfterYears = totalDays % 365
    const months = Math.floor(remainingDaysAfterYears / 30)
    const days = remainingDaysAfterYears % 30
    
    const parts: string[] = []
    if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`)
    if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`)
    if (days > 0 && years === 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`)
    
    const text = parts.length > 0 ? parts.join(' ') : 'today'
    let color = 'text-green-600'
    if (totalDays < 30) color = 'text-red-600'
    else if (totalDays <= 90) color = 'text-yellow-600'
    
    return { text, color }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (error || !cert) {
    return (
      <div className="text-center py-12">
        <Award className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Certification not found</h3>
        <Link to="/" className="text-blue-600 hover:underline">
          Back to certifications
        </Link>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5" />
          Back
        </button>
        
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <MoreVertical className="w-5 h-5 text-gray-600" />
          </button>
          
          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                <Link
                  to={`/certifications/${id}/edit`}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => setShowMenu(false)}
                >
                  <Edit2 className="w-4 h-4" />
                  Edit
                </Link>
                {cert.status !== 'archived' && (
                  <button
                    onClick={() => {
                      setShowMenu(false)
                      archiveMutation.mutate()
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Archive className="w-4 h-4" />
                    Archive
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowMenu(false)
                    setShowDeleteConfirm(true)
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{cert.certName}</h1>
              {getStatusBadge(cert.status)}
            </div>
            <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Award className="w-8 h-8 text-blue-600" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Issuer</p>
                <p className="font-medium text-gray-900">{cert.issuer || 'Not specified'}</p>
              </div>
            </div>

            {cert.state && (
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">State</p>
                  <p className="font-medium text-gray-900">{cert.state}</p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Issue Date</p>
                <p className="font-medium text-gray-900">{formatDate(cert.issueDate)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-500">Expiration Date</p>
                <p className={`font-medium ${
                  cert.status === 'expired' ? 'text-red-600' : 
                  cert.status === 'expiring_soon' ? 'text-yellow-600' : 'text-gray-900'
                }`}>
                  {formatDate(cert.expirationDate)}
                  {cert.expirationDate && (() => {
                    const timeInfo = getTimeUntilExpiration(cert.expirationDate)
                    return <span className={`${timeInfo.color} font-normal`}> ({timeInfo.text})</span>
                  })()}
                </p>
              </div>
            </div>

            {cert.certNumber && (
              <div className="flex items-start gap-3 sm:col-span-2">
                <FileText className="w-5 h-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm text-gray-500">Certification Number</p>
                  <p className="font-medium text-gray-900">{cert.certNumber}</p>
                </div>
              </div>
            )}

            {cert.notes && (
              <div className="sm:col-span-2">
                <p className="text-sm text-gray-500 mb-1">Notes</p>
                <p className="text-gray-700">{cert.notes}</p>
              </div>
            )}
          </div>

          {cert.documentUri && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3">Document</p>
              {imageLoading ? (
                <div className="w-full h-48 bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : documentBlobUrl ? (
                <a href={documentBlobUrl} target="_blank" rel="noopener noreferrer">
                  <img 
                    src={documentBlobUrl} 
                    alt="Certification document"
                    className="max-w-full h-auto rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
                    style={{ maxHeight: '400px' }}
                  />
                </a>
              ) : (
                <p className="text-gray-400 text-sm">Unable to load document image</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Certification?</h3>
            <p className="text-gray-500 mb-6">
              This will permanently delete "{cert.certName}". This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
